import { FirestoreSource, ModuleConnectionLayout, SessionStep } from '@creative-kit/shared';

type MinimalSourceFields = Pick<FirestoreSource, 'fieldIds' | 'defaultFields'>;
type MinimalImageSourceFields = Pick<FirestoreSource, 'attachmentFieldId' | 'imageFieldIds'>;
type MinimalAvailableModuleFields = Pick<FirestoreSource, 'fieldIds' | 'attachmentFieldId'>;
type MinimalOrderedModuleFields = Pick<
  FirestoreSource,
  'fieldIds' | 'attachmentFieldId' | 'imageFieldIds'
>;

export function getModuleLayoutOptions(hasImageColumns: boolean): ModuleConnectionLayout[] {
  return hasImageColumns
    ? ['gallery', 'data', 'image-fields', 'compact']
    : ['gallery', 'data', 'compact'];
}

export function getNextSelectedFields(selectedFields: string[], fieldId: string): string[] {
  return selectedFields.includes(fieldId)
    ? selectedFields.filter((id) => id !== fieldId)
    : [...selectedFields, fieldId];
}

export function getSourceDefaultModuleFields(source: MinimalSourceFields): string[] {
  if (source.defaultFields && source.defaultFields.length > 0) {
    return source.defaultFields;
  }

  return source.fieldIds;
}

export function getStepCompleteTarget(
  currentStepId: string | null | undefined,
  sortedSteps: Pick<SessionStep, 'id'>[],
): { type: 'step'; stepId: string } | { type: 'done' } {
  if (sortedSteps.length === 0) {
    return { type: 'done' };
  }

  if (currentStepId === null || currentStepId === undefined) {
    return { type: 'step', stepId: sortedSteps[0].id };
  }

  const currentStepIndex = sortedSteps.findIndex((step) => step.id === currentStepId);
  if (currentStepIndex === -1 || currentStepIndex === sortedSteps.length - 1) {
    return { type: 'done' };
  }

  return { type: 'step', stepId: sortedSteps[currentStepIndex + 1].id };
}

export function getForcedImageFieldIds(
  source: MinimalImageSourceFields,
  selectedFields: string[],
): string[] {
  const nextFields = [...selectedFields];
  const forcedFieldIds = [
    ...(source.attachmentFieldId ? [source.attachmentFieldId] : []),
    ...(source.imageFieldIds ?? []),
  ];

  forcedFieldIds.forEach((fieldId) => {
    if (!nextFields.includes(fieldId)) {
      nextFields.push(fieldId);
    }
  });

  return nextFields;
}

export function getAvailableModuleFieldIds(source: MinimalAvailableModuleFields): string[] {
  if (!source.attachmentFieldId || source.fieldIds.includes(source.attachmentFieldId)) {
    return source.fieldIds;
  }

  return [...source.fieldIds, source.attachmentFieldId];
}

export function getOrderedModuleFieldIds(source: MinimalOrderedModuleFields): string[] {
  const availableFieldIds = getAvailableModuleFieldIds(source);
  const nonAttachmentFieldIds = availableFieldIds.filter(
    (fieldId) => fieldId !== source.attachmentFieldId,
  );
  const imageFieldIds = new Set(source.imageFieldIds ?? []);
  const orderedImageFields = nonAttachmentFieldIds.filter((fieldId) => imageFieldIds.has(fieldId));
  const orderedNonImageFields = nonAttachmentFieldIds.filter(
    (fieldId) => !imageFieldIds.has(fieldId),
  );

  return [
    ...(source.attachmentFieldId ? [source.attachmentFieldId] : []),
    ...orderedImageFields,
    ...orderedNonImageFields,
  ];
}

export function getVisitCardsLabel(selectedCount: number): string {
  return selectedCount > 1 ? 'Visit Cards' : 'Visit Card';
}

export function getEmptySelectionPlaceholder(isMultiselect: boolean): string {
  return isMultiselect ? 'Add options...' : 'Select...';
}

export function getCreateCardsLabel(selectedCount: number): string {
  return selectedCount > 1 ? `Create ${selectedCount} New Cards` : 'Create New Card';
}

export function getHomeGridColumns(containerWidth: number, section: 'steps' | 'sources'): number {
  if (containerWidth < 500) return 1;
  if (containerWidth < 900) return 2;
  return section === 'steps' ? 4 : 3;
}
